import Link from 'next/link';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Collapse } from '@mui/material';
import { Tooltip } from '@kc-console/common-ui';

import {
  getCollapsedLnbItemButtonStyle,
  getLnbItemButtonStyle,
} from '../../../lib/lnbItemButtonStyles';
import { useServiceListItem } from '../../../model/useServiceListItem';
import ItemContent from './ItemContent';

import type { Menus } from '@kc-console/context';

interface ItemButtonProps {
  collapsed?: boolean;
  depth?: number;
  hasSubMenu?: boolean;
}

export interface MenuListItemProps {
  menu: Menus;
  collapsed?: boolean;
  depth?: number;
  onClick?: () => void;
  subMenu?: Menus[] | null;
}

export default function MenuListItem({
  depth = 1,
  collapsed = false,
  menu,
  onClick,
  ...others
}: MenuListItemProps) {
  const { path, menuName, subMenus, hasSubMenu, isOpen, isActive, handleClick } =
    useServiceListItem({ menu, onClick });

  return (
    <>
      <Tooltip message={collapsed ? menuName : ''} placement="right">
        <StyledItemButton
          LinkComponent={hasSubMenu ? 'button' : Link}
          href={hasSubMenu ? undefined : path}
          collapsed={collapsed}
          depth={depth}
          hasSubMenu={hasSubMenu}
          onClick={handleClick}
          disableRipple
          data-active={isActive ? 'active' : undefined}
          {...others}
        >
          <ItemContent
            collapsed={collapsed}
            menuName={menuName}
            isSelected={isActive}
            hasSubMenu={hasSubMenu}
            isOpen={isOpen}
          />
        </StyledItemButton>
      </Tooltip>
      {hasSubMenu && (
        <Collapse in={isOpen} timeout="auto">
          {subMenus?.map(subMenu => (
            <MenuListItem
              key={subMenu.path}
              depth={depth + 1}
              menu={subMenu}
              collapsed={collapsed}
              onClick={onClick}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

const StyledItemButton = styled(Button, {
  shouldForwardProp: prop => !['collapsed', 'depth', 'hasSubMenu'].includes(prop as string),
})<ItemButtonProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: auto;
  min-height: 40px;
  max-height: 60px;
  border-radius: 0;
  text-transform: none;
  text-align: left;

  ${({ collapsed, depth }) => {
    if (collapsed) {
      return css`
        padding: 0px 16px;
      `;
    }

    switch (depth) {
      case 3:
        return css`
          padding: 9px 16px 9px 56px;
        `;
      case 2:
        return css`
          padding: 9px 16px 9px 40px;
        `;
      default:
        return css`
          padding: 9px 16px 9px 24px;
        `;
    }
  }}

  ${({ theme, collapsed, hasSubMenu }) =>
    collapsed
      ? getCollapsedLnbItemButtonStyle(theme, { hasSubMenu })
      : getLnbItemButtonStyle(theme)}
`;
